import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, Stat, StateView, formatRelative, useAuth, useToast } from "@ozpanel/ui";

// EVERY API token on the platform (Oz, 2026-10-28), beside Token requests — the queue that issues some
// of them. GET /auth/tokens/platform groups them the way the role model does (access.Scope): platform
// administrators, each account, mailbox users, and Other for an owner the role model cannot place —
// listed and labelled, never dropped. DELETE /auth/tokens/platform/{id} revokes any of them at once, with
// every token it created; the dialog asks GET …/{id}/cascade first (a read) so the operator sees how many
// go with it BEFORE confirming. The platform audit log keeps who/when/where/which/cascade; the account's
// own view says only "revoked by the platform operator" and when (internal/server/token_revoke_audit.go).
// Shapes: internal/server/platform_tokens.go, internal/auth/platformtokens.go.
//
// Origin (source, created_by, parent) is recorded since migration 0191; an older token reads "unknown".
// The address a token was last used from is not recorded at all (the per-call addresses are in the audit
// log). The server sends what it does not record in not_recorded. Mail SSO and device access tokens are a
// different store, listed on the Access tokens module page.

interface PlatformToken {
  id: number;
  name: string;
  scopes: string[];
  allowed_ips: string[];
  allow_dangerous: boolean;
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
  owner_user_id: number;
  owner_email: string;
  owner_role: string;
  owner_status: string;
  tenant_id?: number;
  source: "console" | "api" | "cli" | "request" | "unknown";
  created_by_user_id?: number;
  created_by_email?: string;
  parent_token_id?: number;
  parent_token_name?: string;
  request_id?: number;
  approved_by_email?: string;
  expired: boolean;
  no_expiry: boolean;
  unused_30d: boolean;
  refused_reason?: string;
}

interface FlagCounts {
  dangerous: number;
  no_expiry: number;
  unused_30d: number;
  expired: number;
  refused: number;
}

interface TokenGroup {
  kind: "platform" | "tenant" | "mailbox" | "other";
  key: string;
  label: string;
  tenant_id?: number;
  tenant_name?: string;
  tenant_system_user?: string;
  tenant_owner_email?: string;
  tenant_status?: string;
  count: number;
  flags: FlagCounts;
  tokens: PlatformToken[];
}

interface PlatformTokens {
  groups: TokenGroup[];
  total: number;
  flags: FlagCounts;
  unused_after_days: number;
  measured_at: string;
  current_token_id: number;
  not_recorded: string[];
}

interface Cascade {
  id: number;
  descendant_ids: number[];
  count: number;
  includes_current: boolean;
}

const SOURCE_LABEL: Record<PlatformToken["source"], string> = {
  console: "console session",
  api: "created with a token",
  cli: "ozpanel CLI",
  request: "approved request",
  unknown: "unknown",
};

type FlagFilter = "" | "dangerous" | "no_expiry" | "unused_30d" | "expired" | "refused";

const FLAG_LABEL: Record<Exclude<FlagFilter, "">, string> = {
  dangerous: "Dangerous",
  no_expiry: "No expiry",
  unused_30d: "Unused 30 d+",
  expired: "Expired",
  refused: "Would be refused",
};

function hasFlag(t: PlatformToken, f: FlagFilter): boolean {
  switch (f) {
    case "":
      return true;
    case "dangerous":
      return t.allow_dangerous;
    case "refused":
      return !!t.refused_reason;
    default:
      return t[f];
  }
}

function TokenBadges({ t, current, yours }: { t: PlatformToken; current: boolean; yours: boolean }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {current && <StatusPill tone="accent">this request</StatusPill>}
      {yours && !current && <StatusPill tone="neutral">yours</StatusPill>}
      {t.allow_dangerous && <StatusPill tone="warn">dangerous</StatusPill>}
      {t.no_expiry && <StatusPill tone="warn">no expiry</StatusPill>}
      {t.unused_30d && <StatusPill tone="neutral">unused 30 d+</StatusPill>}
      {t.expired && <StatusPill tone="bad">expired</StatusPill>}
      {t.refused_reason && !t.expired && <StatusPill tone="bad">{t.refused_reason}</StatusPill>}
    </span>
  );
}

export function ApiTokensScreen() {
  const { client, who } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["platform-api-tokens"],
    queryFn: () => client.apiFetch<PlatformTokens>(`/auth/tokens/platform`),
    refetchInterval: 30000,
  });
  const [group, setGroup] = React.useState("");
  const [flag, setFlag] = React.useState<FlagFilter>("");
  const [search, setSearch] = React.useState("");
  const [revoking, setRevoking] = React.useState<PlatformToken | null>(null);
  const [selfAck, setSelfAck] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const cascade = useQuery({
    queryKey: ["platform-api-tokens", "cascade", revoking?.id],
    enabled: revoking != null,
    queryFn: () => client.apiFetch<Cascade>(`/auth/tokens/platform/${revoking!.id}/cascade`),
  });
  const d = q.data;
  const current = d?.current_token_id ?? 0;
  const isCurrent = (t: PlatformToken) => current > 0 && t.id === current;
  const isYours = (t: PlatformToken) => !!who && t.owner_email === who.email;

  const needle = search.trim().toLowerCase();
  const shown = (d?.groups ?? [])
    .filter((g) => group === "" || g.key === group)
    .map((g) => ({
      ...g,
      tokens: g.tokens.filter(
        (t) => hasFlag(t, flag) && (needle === "" || t.name.toLowerCase().includes(needle) || t.owner_email.toLowerCase().includes(needle)),
      ),
    }))
    // An empty group stays visible only while nothing narrows the list: "no tokens here" is an answer
    // when looking at everything, and noise when the filter excluded them.
    .filter((g) => g.tokens.length > 0 || (flag === "" && needle === "" && (group === "" || g.key === group)));
  const filtering = group !== "" || flag !== "" || needle !== "";

  const r = revoking;
  // The caller's own token anywhere in what goes — the token itself or one it created — signs it out.
  const rSelf = r ? isCurrent(r) || !!cascade.data?.includes_current : false;
  const revoke = async () => {
    if (!revoking) return;
    setBusy(true);
    try {
      const self = rSelf;
      // expect = the count this dialog showed: if the tree changed since, the server refuses (409) and
      // revokes nothing, and the dialog re-reads the count to confirm again.
      const expect = cascade.data ? `&expect=${cascade.data.count}` : "";
      const res = await client.apiFetch<{ cascaded: number }>(`/auth/tokens/platform/${revoking.id}?confirm_self=${self ? "true" : "false"}${expect}`, { method: "DELETE" });
      notify({
        kind: "success",
        title: `Token "${revoking.name}" revoked`,
        body: `It belonged to ${revoking.owner_email} and no longer authenticates.${res.cascaded ? ` ${res.cascaded} token${res.cascaded === 1 ? "" : "s"} it created went with it.` : ""}`,
      });
      setRevoking(null);
      setSelfAck(false);
      void qc.invalidateQueries({ queryKey: ["platform-api-tokens"] });
      void qc.invalidateQueries({ queryKey: ["account-api-tokens"] });
      void qc.invalidateQueries({ queryKey: ["my-api-tokens"] });
    } catch (e) {
      notify({ kind: "error", title: "Could not revoke that token", body: e instanceof Error ? e.message : String(e) });
      void cascade.refetch();
    } finally {
      setBusy(false);
    }
  };

  const cols = (g: TokenGroup): DataListColumn<PlatformToken>[] => [
    {
      key: "name",
      label: "Token",
      sortable: true,
      render: (t) => (
        <span className="inline-flex flex-col gap-1">
          <span className="font-mono text-[12.5px]">
            {t.name} <span className="text-faint">#{t.id}</span>
          </span>
          <TokenBadges t={t} current={isCurrent(t)} yours={isYours(t)} />
        </span>
      ),
    },
    {
      key: "owner_email",
      label: "Owner",
      sortable: true,
      render: (t) => (
        <span className="inline-flex flex-col">
          <span className="font-mono text-[12px]">{t.owner_email}</span>
          <span className="text-[11.5px] text-muted">
            {t.owner_role}
            {t.owner_status !== "active" ? ` · ${t.owner_status}` : ""}
            {g.kind !== "tenant" && t.tenant_id != null ? ` · account ${t.tenant_id}` : ""}
          </span>
        </span>
      ),
    },
    {
      key: "scopes",
      label: "Allows",
      value: (t) => t.scopes.join(" "),
      render: (t) => <span className="font-mono text-[11.5px]">{t.scopes.join(" ") || "no scopes"}</span>,
    },
    {
      key: "allowed_ips",
      label: "From",
      value: (t) => t.allowed_ips.join(" "),
      render: (t) => <span className="font-mono text-[11.5px]">{t.allowed_ips.length ? t.allowed_ips.join(" ") : "anywhere"}</span>,
    },
    { key: "created_at", label: "Created", sortable: true, render: (t) => formatRelative(t.created_at) },
    {
      key: "expires_at",
      label: "Expires",
      sortable: true,
      value: (t) => t.expires_at ?? "",
      render: (t) => (t.expires_at ? <span className={t.expired ? "text-bad" : undefined}>{formatRelative(t.expires_at)}</span> : <span className="text-warn">never</span>),
    },
    {
      key: "last_used_at",
      label: "Last used",
      sortable: true,
      value: (t) => t.last_used_at ?? "",
      render: (t) => (t.last_used_at ? formatRelative(t.last_used_at) : <span className="text-muted">never</span>),
    },
    {
      key: "source",
      label: "Made by",
      render: (t) => (
        <span className="inline-flex flex-col text-[11.5px]">
          <span className={t.source === "unknown" ? "text-muted" : undefined} title={t.source === "unknown" ? "Created before token origins were recorded" : undefined}>
            {SOURCE_LABEL[t.source] ?? t.source}
            {t.created_by_email ? ` · ${t.created_by_email}` : ""}
          </span>
          {t.parent_token_id != null && (
            <span className="font-mono text-muted">
              from {t.parent_token_name || "token"} #{t.parent_token_id}
            </span>
          )}
          {t.request_id != null && (
            <span className="text-muted">
              request #{t.request_id}
              {t.approved_by_email ? `, approved by ${t.approved_by_email}` : ""}
            </span>
          )}
        </span>
      ),
    },
  ];

  const groupHint = (g: TokenGroup) => {
    const parts = [`${g.count} token${g.count === 1 ? "" : "s"}`];
    if (g.flags.dangerous) parts.push(`${g.flags.dangerous} dangerous`);
    if (g.flags.no_expiry) parts.push(`${g.flags.no_expiry} no expiry`);
    if (g.flags.expired) parts.push(`${g.flags.expired} expired`);
    return parts.join(" · ");
  };

  return (
    <div>
      <PageHeader
        title="API tokens"
        subtitle="Every API token on the platform, by owner — never a value. Revoking one takes effect on its next call."
      />
      <StateView loading={q.isLoading} error={q.error} errorContext="the platform's API tokens">
        {d && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
              <Stat label="Tokens" value={d.total} />
              <Stat label="Dangerous" value={d.flags.dangerous} tone={d.flags.dangerous ? "warn" : undefined} />
              <Stat label="No expiry" value={d.flags.no_expiry} tone={d.flags.no_expiry ? "warn" : undefined} />
              <Stat label={`Unused ${d.unused_after_days} d+`} value={d.flags.unused_30d} />
              <Stat
                label="Expired, still present"
                value={d.flags.expired}
                tone={d.flags.expired ? "crit" : undefined}
                hint={d.flags.refused > d.flags.expired ? `${d.flags.refused - d.flags.expired} more would be refused (owner or account)` : undefined}
              />
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-2">
              <select
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                aria-label="Group"
                className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg"
              >
                <option value="">All groups</option>
                {d.groups.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.kind === "tenant" ? `Account: ${g.label}` : g.label.split(" — ")[0]} ({g.count})
                  </option>
                ))}
              </select>
              <select
                value={flag}
                onChange={(e) => setFlag(e.target.value as FlagFilter)}
                aria-label="Flag"
                className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg"
              >
                <option value="">Any flag</option>
                {(Object.keys(FLAG_LABEL) as Exclude<FlagFilter, "">[]).map((f) => (
                  <option key={f} value={f}>
                    {FLAG_LABEL[f]}
                  </option>
                ))}
              </select>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or owner email"
                className="min-w-[220px] flex-1 rounded-lg border border-line2 bg-bg px-3 py-1 text-[12.5px] outline-none focus:border-accent"
              />
              {filtering && (
                <Button size="sm" variant="ghost" onClick={() => { setGroup(""); setFlag(""); setSearch(""); }}>
                  Clear
                </Button>
              )}
            </div>

            {shown.length === 0 && <div className="py-6 text-[13px] text-muted">No token matches.</div>}
            <div className="space-y-4">
              {shown.map((g) => (
                <Section
                  key={g.key}
                  title={
                    g.kind === "tenant" && g.tenant_id != null ? (
                      <span>
                        <Link to={`/tenants/${g.tenant_id}`} className="hover:text-accent">
                          {g.label}
                        </Link>
                      </span>
                    ) : (
                      g.label
                    )
                  }
                  actions={<span className="text-[12px] text-muted">{groupHint(g)}</span>}
                  bodyClassName="p-0"
                >
                  {g.kind === "tenant" && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-line px-4 py-2 text-[11.5px] text-muted">
                      <span>system user <span className="font-mono text-fg">{g.tenant_system_user || "—"}</span></span>
                      <span>contact <span className="font-mono text-fg">{g.tenant_owner_email || "—"}</span></span>
                      {g.tenant_status && g.tenant_status !== "active" && <StatusPill tone="warn">{g.tenant_status}</StatusPill>}
                    </div>
                  )}
                  <div className="p-4">
                    <DataList
                      items={g.tokens}
                      columns={cols(g)}
                      getRowKey={(t) => String(t.id)}
                      emptyMessage="No tokens."
                      rowActions={(t) => (
                        <Button size="sm" variant="danger" disabled={busy} onClick={() => { setSelfAck(false); setRevoking(t); }}>
                          Revoke
                        </Button>
                      )}
                    />
                  </div>
                </Section>
              ))}
            </div>

            <div className="mt-4 space-y-1 text-[11.5px] leading-relaxed text-muted">
              <div>
                Mail sign-in and device access tokens (IMAP/SMTP/CalDAV logins, Apple Mail profiles) are a different store, not
                listed here: <Link to="/modules/tokens" className="text-accent">Access tokens</Link>.
              </div>
              <div>
                Not recorded: {d.not_recorded.join("; ")}. Your own tokens, and what each was used for, are on{" "}
                <Link to="/security" className="text-accent">Your security</Link>; requests waiting for a decision on{" "}
                <Link to="/token-requests" className="text-accent">Token requests</Link>.
              </div>
              <div>Flags measured {formatRelative(d.measured_at)}.</div>
            </div>
          </>
        )}
      </StateView>

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => {
          if (!o) {
            setRevoking(null);
            setSelfAck(false);
          }
        }}
        title={r ? `Revoke "${r.name}"?` : "Revoke token"}
        confirmLabel="Revoke"
        destructive
        pending={busy}
        confirmDisabled={cascade.isLoading || !!cascade.error || (rSelf && !selfAck)}
        onConfirm={() => void revoke()}
        body={
          r ? (
            <div className="space-y-2 text-[12.5px]">
              <div>
                This token belongs to <span className="font-mono">{r.owner_email}</span> ({r.owner_role}
                {r.tenant_id != null ? `, account ${r.tenant_id}` : ""}). Anything using it stops working on its next call, and it
                cannot be restored — a new token has to be created.
              </div>
              <div className="font-mono text-[11.5px]">scopes: {r.scopes.join(" ") || "none"}{r.allow_dangerous ? " · DANGEROUS" : ""}</div>
              {cascade.isLoading && <div className="text-muted">Checking what this token created…</div>}
              {cascade.error ? (
                <div className="text-bad">What this token created could not be read, so it cannot be revoked from here right now.</div>
              ) : null}
              {cascade.data && cascade.data.count > 0 && (
                <div className="rounded border border-warn/40 px-3 py-2 text-warn">
                  This also revokes {cascade.data.count} token{cascade.data.count === 1 ? "" : "s"} it created
                  <span className="font-mono text-[11.5px]"> (#{cascade.data.descendant_ids.join(", #")})</span>.
                </div>
              )}
              {rSelf ? (
                <label className="flex items-start gap-2 rounded border border-bad/40 px-3 py-2 text-bad">
                  <input type="checkbox" checked={selfAck} onChange={(e) => setSelfAck(e.target.checked)} className="mt-0.5" />
                  <span>
                    {isCurrent(r)
                      ? "This is the token this console is signed in with."
                      : "The token this console is signed in with was created by this one and goes with it."}{" "}
                    Revoking it signs you out at once.
                  </span>
                </label>
              ) : (
                isYours(r) && <div className="text-warn">This is one of your own tokens.</div>
              )}
            </div>
          ) : null
        }
      />
    </div>
  );
}
